// Keep the full task context alongside the joined segment set. The active
// task drives project-specific tools; a save always uses its segment's owner.
export const getSegmentTask = (state, segment) => {
	// Ordinary editors always use the current task, including updated options
	// after a split/join. Initial task snapshots are only for joined sessions.
	if (state.taskids?.length <= 1 || state.joinedTasks?.length <= 1) return state.task;
	if (String(state.task?.id) === String(segment?.task)) return state.task;
	return state.joinedTasks?.find((task) => String(task.id) === String(segment?.task))
		?? state.joinedTasks?.find((task) => task.parent && String(task.parent) === String(segment?.task))
		?? state.task;
};

export const getSegmentContext = (state, segment, fallbackTask = state.task) => {
	const task = getSegmentTask({ ...state, task: fallbackTask }, segment);
	return {
		task: segment?.task ?? task.id,
		project: task.project.id,
		file: task.file.id,
		step: task.step,
		stepOrdr: task.stepOrdr,
		targetLang: task.targetLang,
	};
};

export const getActiveTaskState = (state, segment) => {
	const task = getSegmentTask(state, segment);
	if (!task || task.id === state.task?.id) return {};
	return {
		task: {
			...task,
			...(state.openedAsView ? state.viewTaskContext : {}),
			ratingSettings: state.task?.ratingSettings,
			options: { [task.id]: state.joinedSegmentIds ?? task.options?.[task.id] ?? [] },
		},
	};
};

export const getTaskSegmentIds = (task) =>
	task.options?.[task.id] ?? task.options?.[task.parent] ?? Object.values(task.options ?? {})[0] ?? [];

// The same source segment may have a translation at multiple workflow steps.
// Keep these task versions in separate selectable scopes rather than letting
// one overwrite another in the segment map (which is keyed by source id).
export const getJoinedScope = (tasks) => {
	const owners = new Map();
	const hasOverlappingSteps = tasks.some((task) => getTaskSegmentIds(task).some((id) => {
		const owner = owners.get(String(id));
		owners.set(String(id), task);
		return owner && String(owner.step) !== String(task.step);
	}));
	return hasOverlappingSteps ? {
		joinedTaskFocus: tasks[0].id,
		joinedSegmentIds: getTaskSegmentIds(tasks[0]),
	} : {};
};

export const haveSameLanguagePair = (tasks) => tasks.every((task) =>
    task.sourceLang === tasks[0].sourceLang && task.targetLang === tasks[0].targetLang
);

export const getEditorSegmentIds = (state) =>
	state.taskids?.length > 1 && state.joinedSegmentIds
		? state.joinedSegmentIds
		: getTaskSegmentIds(state.task ?? {});

export const getEditorScopeKey = (state) =>
	(state.joinedTaskFocus ? [state.joinedTaskFocus] : state.taskids?.length ? state.taskids : [state.task?.id])
		.map(String).join('-');
